import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeftOutlined, CheckOutlined, DeleteOutlined, DownOutlined, DownloadOutlined, EditOutlined, EyeOutlined, PlayCircleOutlined, RedoOutlined, ThunderboltOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Card, DatePicker, Dropdown, Form, Input, Menu, message, Modal, Popconfirm, Select, Space, Switch, Table, Tag, Typography, Upload } from "antd";
import moment from "moment";
import AiVideoProjectForm, { AiVideoUploadSettings, aiVideoProjectPayload, projectFormValues } from "../components/aiVideo/AiVideoProjectForm";
import { aiVideoErrorMessage, aiVideoStageLabel, createAiVideoProjectPoller } from "../components/aiVideo/AiVideoProgress";
import { AUTH_TOKEN_KEY } from "../services/apiClient";
import { aiVideoApi } from "../services/aiVideoApi";
import { promptApi } from "../services/promptApi";
import { googleWorkspaceApi } from "../services/googleWorkspaceApi";
import { useSessionProfile } from "../services/SessionProfileContext";
import { aiVideoProjectActions, aiVideoQuotaLabel, openAiVideoDownload, safeAiVideoErrorMessage } from "./AiVideoProjects";

const { Text, Title } = Typography;
const ACTIVE_EXECUTION_STATUSES = new Set(["PENDING_RETRY", "QUEUED", "LEASED", "RUNNING"]);

export function aiVideoExecutionCode(project, row) {
  if (project?.scriptSource === "GOOGLE_SHEETS") return row?.sheetVideoId || "-";
  return row?.generationNumber == null ? "-" : `#${row.generationNumber}`;
}

export function aiVideoExecutionActionAvailability(row, executions = []) {
  const completed = row?.renderStatus === "COMPLETED";
  const active = ACTIVE_EXECUTION_STATUSES.has(row?.renderStatus);
  const hasUploadTarget = Boolean(row?.channelType && row?.channelId);
  const sharedContentLocked = executions.some((item) => item.jobId === row?.jobId && item.videoId);
  const edit = completed && !row?.videoId && !sharedContentLocked;
  return {
    retry: row?.renderStatus === "FAILED" || row?.renderStatus === "CANCELLED",
    preview: completed,
    download: completed,
    edit,
    viewContent: completed && !edit,
    destination: completed && !row?.videoId,
    confirm: completed && !row?.videoId && hasUploadTarget,
    delete: !active,
  };
}

export default function AiVideoProjectDetail() {
  const { id } = useParams();
  const { profile, status } = useSessionProfile();
  const [projectForm] = Form.useForm();
  const [uploadForm] = Form.useForm();
  const [contentForm] = Form.useForm();
  const [destinationForm] = Form.useForm();
  const mounted = useRef(true);
  const errorNotified = useRef(false);
  const pollerRef = useRef(null);
  const identity = useRef({ id, token: localStorage.getItem(AUTH_TOKEN_KEY), status, profileId: profile.id });
  identity.current = { id, token: localStorage.getItem(AUTH_TOKEN_KEY), status, profileId: profile.id };
  const [project, setProject] = useState(null);
  const [providers, setProviders] = useState([]);
  const [workspaceConnections, setWorkspaceConnections] = useState([]);
  const [googleTrigger, setGoogleTrigger] = useState(null);
  const [youtubeChannels, setYoutubeChannels] = useState([]);
  const [tiktokAccounts, setTiktokAccounts] = useState([]);
  const [facebookPages, setFacebookPages] = useState([]);
  const [quota, setQuota] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null);
  const [pendingExecutionAction, setPendingExecutionAction] = useState(null);
  const [deletingExecutionId, setDeletingExecutionId] = useState(null);
  const [confirmingUploadId, setConfirmingUploadId] = useState(null);
  const [retryingExecutionId, setRetryingExecutionId] = useState(null);
  const [selectedExecutionIds, setSelectedExecutionIds] = useState([]);
  const [errorExecution, setErrorExecution] = useState(null);
  const [contentExecution, setContentExecution] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentSaving, setContentSaving] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [destinationExecution, setDestinationExecution] = useState(null);
  const [destinationPlatform, setDestinationPlatform] = useState(null);
  const [destinationLoading, setDestinationLoading] = useState(false);
  const [destinationSaving, setDestinationSaving] = useState(false);
  const [destinationThumbnailFile, setDestinationThumbnailFile] = useState(null);
  const [destinationThumbnailPreview, setDestinationThumbnailPreview] = useState(null);

  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    if (status !== "ready") return undefined;
    const routeId = id;
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    setProject(null);
    setLoading(true);
    const isCurrent = () => mounted.current
      && identity.current.id === routeId
      && localStorage.getItem(AUTH_TOKEN_KEY) === token
      && identity.current.status === "ready"
      && identity.current.profileId === profile.id;
    const poller = createAiVideoProjectPoller({
      fetchProject: async (projectId) => {
        const [nextProject, quotaResult, executionResult] = await Promise.all([
          aiVideoApi.projects.get(projectId),
          aiVideoApi.quota().then((value) => ({ value })).catch(() => ({ value: null })),
          aiVideoApi.projects.executions(projectId).then((value) => ({ value })).catch(() => ({ value: [] })),
        ]);
        const nextQuota = quotaResult.value;
        return { ...nextProject, __quota: nextQuota, __executions: executionResult.value };
      },
      onProject: (response) => {
        const { __quota: nextQuota, __executions: nextExecutions, ...nextProject } = response || {};
        if (!nextProject || String(nextProject.id) !== String(routeId)) return;
        errorNotified.current = false;
        setProject(nextProject);
        setQuota(nextQuota || null);
        setExecutions(Array.isArray(nextExecutions) ? nextExecutions : []);
        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
        if (!errorNotified.current) {
          errorNotified.current = true;
          message.error(safeAiVideoErrorMessage(error, "Không thể tải chi tiết dự án AI Video."));
        }
      },
      isCurrent,
      intervalMs: 3000,
    });
    pollerRef.current = poller;
    poller.start(routeId);
    return () => {
      poller.stop();
      if (pollerRef.current === poller) pollerRef.current = null;
    };
  }, [id, profile.id, status]);

  useEffect(() => {
    if (status !== "ready") return undefined;
    let current = true;
    const request = Promise.all([
      profile.aiVideoPlanMode === "BYOK" ? promptApi.providers.list() : Promise.resolve([]),
      aiVideoApi.youtubeChannels(),
      aiVideoApi.tiktokAccounts(),
      aiVideoApi.facebookPages(),
      googleWorkspaceApi.connections(),
      googleWorkspaceApi.triggers.list(),
    ]);
    request.then(([nextProviders, nextYoutubeChannels, nextTiktokAccounts, nextFacebookPages, nextConnections, nextTriggers]) => {
      if (!current || !mounted.current) return;
      setProviders(Array.isArray(nextProviders) ? nextProviders : []);
      setYoutubeChannels(Array.isArray(nextYoutubeChannels) ? nextYoutubeChannels : []);
      setTiktokAccounts(Array.isArray(nextTiktokAccounts) ? nextTiktokAccounts : []);
      setFacebookPages(Array.isArray(nextFacebookPages) ? nextFacebookPages : []);
      setWorkspaceConnections(Array.isArray(nextConnections) ? nextConnections : []);
      setGoogleTrigger((Array.isArray(nextTriggers) ? nextTriggers : []).find((item) => String(item.aiVideoProjectId) === String(id)) || null);
    }).catch((error) => {
      if (current && mounted.current) message.error(safeAiVideoErrorMessage(error, "Không thể tải danh sách provider."));
    });
    return () => { current = false; };
  }, [id, profile.aiVideoPlanMode, status]);

  const actions = useMemo(() => aiVideoProjectActions(project, profile, quota, providers), [project, profile, providers, quota]);

  const refreshProjectAndQuota = async () => {
    const [nextProject, nextQuota] = await Promise.all([aiVideoApi.projects.get(id), aiVideoApi.quota()]);
    if (!mounted.current || identity.current.id !== id || identity.current.status !== "ready") return;
    if (String(nextProject?.id) === String(id)) setProject(nextProject);
    setQuota(nextQuota || null);
    return nextProject;
  };

  const restartPolling = (confirmedProject) => {
    if (pollerRef.current && mounted.current && identity.current.id === id && identity.current.status === "ready") {
      pollerRef.current.start(id, confirmedProject?.status);
    }
  };

  const update = async (payload) => {
    if (!actions.canEdit || pending) return;
    pollerRef.current?.stop();
    setPending("update");
    let confirmedProject = project;
    try {
      const { googleSheetsConfig, ...projectPayload } = payload;
      await aiVideoApi.projects.update(id, projectPayload);
      if ((payload.projectType === "GOOGLE_SHEETS" || payload.scriptSource === "GOOGLE_SHEETS") && googleSheetsConfig) {
        const savedTrigger = googleTrigger?.id
          ? await googleWorkspaceApi.triggers.update(googleTrigger.id, { ...googleSheetsConfig, aiVideoProjectId: id })
          : await googleWorkspaceApi.triggers.create({ ...googleSheetsConfig, aiVideoProjectId: id });
        setGoogleTrigger(savedTrigger);
      }
      if (!mounted.current) return;
      confirmedProject = await refreshProjectAndQuota();
      message.success("Đã lưu dự án.");
    } catch (error) {
      if (mounted.current) message.error(safeAiVideoErrorMessage(error, "Không thể lưu dự án."));
    } finally {
      if (mounted.current) {
        setPending(null);
        restartPolling(confirmedProject);
      }
    }
  };

  const saveAll = async () => {
    if (!actions.canEdit || pending) return;
    try {
      const [projectFields, uploadFields] = await Promise.all([
        projectForm.validateFields(),
        uploadForm.validateFields(),
      ]);
      const payload = aiVideoProjectPayload({
        ...projectFormValues(project, profile, providers),
        ...projectFields,
        ...uploadFields,
      }, profile, providers);
      await update(payload);
    } catch (error) {
      if (!error?.errorFields && mounted.current) {
        message.error(safeAiVideoErrorMessage(error, "Không thể lưu toàn bộ dự án."));
      }
    }
  };

  const runAction = async (action) => {
    const permission = { start: "canStart", stop: "canStop", generate: "canGenerate" }[action];
    if (!permission || !actions[permission] || pending) return;
    pollerRef.current?.stop();
    setPending(action);
    let confirmedProject = project;
    try {
      if (action === "generate" && (project?.projectType === "GOOGLE_SHEETS"
          || project?.scriptSource === "GOOGLE_SHEETS")) {
        if (!googleTrigger?.id) throw new Error("Dự án chưa có cấu hình Google Sheets");
        await googleWorkspaceApi.triggers.run(googleTrigger.id);
      } else {
        await aiVideoApi.projects[action](id);
      }
      if (!mounted.current) return;
      confirmedProject = await refreshProjectAndQuota();
      message.success(action === "stop"
        ? "Đã dừng dự án."
        : action === "generate"
          ? "Đã tạo lần chạy thủ công và đưa vào hàng đợi render."
          : project?.projectType === "GOOGLE_SHEETS"
            ? "Đã kích hoạt theo dõi Google Sheets."
            : project?.scriptSource === "GOOGLE_SHEETS"
              ? "Đã bật dự án; hệ thống sẽ lấy một kịch bản READY từ Google Sheets theo lịch."
            : "Đã bật dự án; hệ thống sẽ tự tạo video theo lịch.");
    } catch (error) {
      if (mounted.current) {
        message.error(safeAiVideoErrorMessage(error));
        try { confirmedProject = await refreshProjectAndQuota(); } catch (_) { /* keep the last confirmed state */ }
      }
    } finally {
      if (mounted.current) {
        setPending(null);
        restartPolling(confirmedProject);
      }
    }
  };

  const accessExecutionVideo = async (row, action) => {
    if (row?.renderStatus !== "COMPLETED" || pendingExecutionAction) return;
    setPendingExecutionAction({ id: row.id, action });
    try {
      const result = action === "preview"
        ? await aiVideoApi.projects.executionPreview(id, row.id)
        : await aiVideoApi.projects.executionDownload(id, row.id);
      if (!mounted.current) return;
      if (action === "preview") {
        const previewUrl = new URL(String(result?.url || ""));
        if (!["http:", "https:"].includes(previewUrl.protocol)) throw new Error("AI_VIDEO_PREVIEW_INVALID");
        const link = document.createElement("a");
        link.href = previewUrl.toString();
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        openAiVideoDownload(result?.url);
      }
    } catch (error) {
      if (mounted.current) message.error(safeAiVideoErrorMessage(error, action === "preview" ? "Không thể xem video." : "Không thể tải video."));
    } finally {
      if (mounted.current) setPendingExecutionAction(null);
    }
  };

  const deleteExecution = async (row) => {
    if (!row?.id || deletingExecutionId || ["QUEUED", "LEASED", "RUNNING"].includes(row.renderStatus)) return;
    setDeletingExecutionId(row.id);
    try {
      await aiVideoApi.projects.deleteExecution(id, row.id);
      if (!mounted.current) return;
      setExecutions((current) => current.filter((item) => item.id !== row.id));
      setSelectedExecutionIds((current) => current.filter((executionId) => executionId !== row.id));
      message.success("Đã xoá video/bản ghi.");
    } catch (error) {
      if (mounted.current) message.error(safeAiVideoErrorMessage(error, "Không thể xoá bản ghi lỗi."));
    } finally {
      if (mounted.current) setDeletingExecutionId(null);
    }
  };

  const deleteSelectedExecutions = async () => {
    if (!selectedExecutionIds.length || deletingExecutionId) return;
    const selected = new Set(selectedExecutionIds);
    setDeletingExecutionId("BULK");
    try {
      await aiVideoApi.projects.deleteExecutions(id, selectedExecutionIds);
      if (!mounted.current) return;
      setExecutions((current) => current.filter((item) => !selected.has(item.id)));
      setSelectedExecutionIds([]);
      message.success(`Đã xoá ${selected.size} video/bản ghi.`);
    } catch (error) {
      if (mounted.current) message.error(safeAiVideoErrorMessage(error, "Không thể xoá các bản ghi đã chọn."));
    } finally {
      if (mounted.current) setDeletingExecutionId(null);
    }
  };

  const confirmUploads = async (rows, pendingId) => {
    const executionIds = rows.filter((row) => row.renderStatus === "COMPLETED" && !row.videoId).map((row) => row.id);
    if (!executionIds.length || confirmingUploadId) return;
    setConfirmingUploadId(pendingId);
    try {
      const updated = await aiVideoApi.projects.confirmExecutionUploads(id, executionIds);
      if (!mounted.current) return;
      const byId = new Map((Array.isArray(updated) ? updated : []).map((row) => [row.id, row]));
      setExecutions((current) => current.map((row) => byId.get(row.id) || row));
      message.success(`Đã xác nhận ${executionIds.length} bản ghi; Worker Agent có thể nhận để upload.`);
    } catch (error) {
      if (mounted.current) message.error(safeAiVideoErrorMessage(error, "Không thể xác nhận upload."));
    } finally {
      if (mounted.current) setConfirmingUploadId(null);
    }
  };

  const retryExecution = async (row) => {
    if (!row?.id || retryingExecutionId || !["FAILED", "CANCELLED"].includes(row.renderStatus)) return;
    const waitingChannels = executions.filter((item) => item.jobId === row.jobId).length;
    pollerRef.current?.stop();
    setRetryingExecutionId(row.id);
    let confirmedProject = project;
    try {
      const retryJob = await aiVideoApi.projects.retryExecution(id, row.id);
      const [nextProject, nextExecutions, nextQuota] = await Promise.all([
        aiVideoApi.projects.get(id), aiVideoApi.projects.executions(id), aiVideoApi.quota(),
      ]);
      if (!mounted.current) return;
      confirmedProject = nextProject;
      setProject(nextProject);
      setExecutions(Array.isArray(nextExecutions) ? nextExecutions : []);
      setQuota(nextQuota || null);
      setErrorExecution(null);
      message.success(retryJob?.status === "PENDING_RETRY"
        ? "Đã thêm video vào hàng đợi chạy lại; hệ thống sẽ tự chạy khi project rảnh."
        : `Đã chạy lại video cho ${waitingChannels} kênh đang đợi; hệ thống sẽ tái sử dụng các bước hợp lệ.`);
    } catch (error) {
      if (mounted.current) message.error(safeAiVideoErrorMessage(error, "Không thể chạy lại video."));
    } finally {
      if (mounted.current) {
        setRetryingExecutionId(null);
        restartPolling(confirmedProject);
      }
    }
  };

  const closeContentEditor = () => {
    if (thumbnailPreview?.startsWith("blob:")) URL.revokeObjectURL(thumbnailPreview);
    setContentExecution(null);
    setThumbnailFile(null);
    setThumbnailPreview(null);
    contentForm.resetFields();
  };

  const openContentEditor = async (row) => {
    if (!row?.id || row.renderStatus !== "COMPLETED" || contentLoading) return;
    setContentLoading(true);
    try {
      const content = await aiVideoApi.projects.executionContent(id, row.id);
      if (!mounted.current) return;
      contentForm.setFieldsValue({ title: content?.title || "", keywords: content?.keywords || "", description: content?.description || "" });
      setThumbnailFile(null);
      setThumbnailPreview(content?.thumbnailUrl || null);
      setContentExecution(row);
    } catch (error) {
      if (mounted.current) message.error(safeAiVideoErrorMessage(error, "Không thể tải nội dung video."));
    } finally {
      if (mounted.current) setContentLoading(false);
    }
  };

  const chooseThumbnail = (file) => {
    if (!file.type?.startsWith("image/")) {
      message.error("Thumbnail phải là file ảnh.");
      return Upload.LIST_IGNORE;
    }
    if (file.size > 10 * 1024 * 1024) {
      message.error("Thumbnail không được vượt quá 10 MB.");
      return Upload.LIST_IGNORE;
    }
    if (thumbnailPreview?.startsWith("blob:")) URL.revokeObjectURL(thumbnailPreview);
    setThumbnailFile(file);
    setThumbnailPreview(URL.createObjectURL(file));
    return false;
  };

  const saveContentEditor = async () => {
    if (!contentExecution || contentSaving) return;
    try {
      const values = await contentForm.validateFields();
      const formData = new FormData();
      formData.append("title", values.title.trim());
      formData.append("keywords", values.keywords.trim());
      formData.append("description", values.description.trim());
      if (thumbnailFile) formData.append("thumbnail", thumbnailFile);
      setContentSaving(true);
      const content = await aiVideoApi.projects.updateExecutionContent(id, contentExecution.id, formData);
      if (!mounted.current) return;
      setThumbnailPreview(content?.thumbnailUrl || thumbnailPreview);
      message.success("Đã lưu tiêu đề, từ khoá, mô tả và thumbnail dùng chung cho video.");
      closeContentEditor();
    } catch (error) {
      if (!error?.errorFields && mounted.current) message.error(safeAiVideoErrorMessage(error, "Không thể lưu nội dung video."));
    } finally {
      if (mounted.current) setContentSaving(false);
    }
  };

  const destinationDefinition = {
    YOUTUBE: { label: "YouTube", fieldLabel: "Kênh YouTube", items: youtubeChannels, keys: ["channelTitle", "channelName", "channelId"] },
    FACEBOOK: { label: "Facebook Page", fieldLabel: "Facebook Page", items: facebookPages, keys: ["pageName", "pageId", "pageUrl"] },
    TIKTOK: { label: "TikTok", fieldLabel: "Tài khoản TikTok", items: tiktokAccounts, keys: ["displayName", "username", "accountId"] },
  };
  const destinationLabel = (item, keys) => keys.map((key) => item?.[key]).find(Boolean) || item?.id || "Không tên";
  const closeDestinationModal = () => {
    if (destinationThumbnailPreview?.startsWith("blob:")) URL.revokeObjectURL(destinationThumbnailPreview);
    setDestinationExecution(null);
    setDestinationPlatform(null);
    setDestinationThumbnailFile(null);
    setDestinationThumbnailPreview(null);
    destinationForm.resetFields();
  };
  const openDestinationModal = async (row, platform) => {
    if (!row?.id || row.renderStatus !== "COMPLETED" || row.videoId || destinationLoading) return;
    setDestinationLoading(true);
    try {
      const content = await aiVideoApi.projects.executionContent(id, row.id);
      if (!mounted.current) return;
      destinationForm.setFieldsValue({
        channelId: undefined,
        title: content?.title || "",
        keywords: content?.keywords || "",
        description: content?.description || "",
        uploadNow: true,
        scheduledFor: null,
      });
      setDestinationThumbnailFile(null);
      setDestinationThumbnailPreview(content?.thumbnailUrl || null);
      setDestinationPlatform(platform);
      setDestinationExecution(row);
    } catch (error) {
      if (mounted.current) message.error(safeAiVideoErrorMessage(error, "Không thể tải nội dung video."));
    } finally {
      if (mounted.current) setDestinationLoading(false);
    }
  };
  const chooseDestinationThumbnail = (file) => {
    if (!file.type?.startsWith("image/")) { message.error("Thumbnail phải là file ảnh."); return Upload.LIST_IGNORE; }
    if (file.size > 10 * 1024 * 1024) { message.error("Thumbnail không được vượt quá 10 MB."); return Upload.LIST_IGNORE; }
    if (destinationThumbnailPreview?.startsWith("blob:")) URL.revokeObjectURL(destinationThumbnailPreview);
    setDestinationThumbnailFile(file);
    setDestinationThumbnailPreview(URL.createObjectURL(file));
    return false;
  };
  const saveDestinationUpload = async () => {
    if (!destinationExecution || !destinationPlatform || destinationSaving) return;
    try {
      const values = await destinationForm.validateFields();
      const formData = new FormData();
      formData.append("channelType", destinationPlatform);
      formData.append("channelId", values.channelId);
      formData.append("title", values.title.trim());
      formData.append("keywords", values.keywords.trim());
      formData.append("description", values.description.trim());
      formData.append("uploadNow", String(Boolean(values.uploadNow)));
      if (!values.uploadNow) formData.append("scheduledFor", values.scheduledFor.format("YYYY-MM-DDTHH:mm:ss"));
      if (destinationThumbnailFile) formData.append("thumbnail", destinationThumbnailFile);
      setDestinationSaving(true);
      const updated = await aiVideoApi.projects.enqueueExecutionUpload(id, destinationExecution.id, formData);
      if (!mounted.current) return;
      setExecutions((current) => current.some((row) => row.id === updated?.id)
        ? current.map((row) => row.id === updated.id ? updated : row)
        : [updated, ...current]);
      message.success(values.uploadNow ? "Đã thêm một bản ghi upload mới vào queue." : "Đã thêm một bản ghi upload mới theo lịch.");
      closeDestinationModal();
    } catch (error) {
      if (!error?.errorFields && mounted.current) message.error(safeAiVideoErrorMessage(error, "Không thể tạo hàng đợi upload."));
    } finally {
      if (mounted.current) setDestinationSaving(false);
    }
  };

  if (loading && !project) return <div className="page-heading">Đang tải dự án AI Video...</div>;
  if (!project) return <div className="page-heading"><div><Title level={3}>Không thể hiển thị dự án</Title><Link to="/video-tools/ai-video">Quay lại danh sách</Link></div></div>;

  const owner = profile?.id && String(project.ownerId) === String(profile.id);
  const formReadOnly = !owner || !actions.canEdit;
  const contentReadOnly = Boolean(contentExecution && !aiVideoExecutionActionAvailability(contentExecution, executions).edit);
  const formatTime = (value) => value && moment(value).isValid() ? moment(value).format("DD/MM/YYYY HH:mm:ss") : "-";
  const executionStatus = (row) => {
    if (row.renderStatus === "FAILED" || row.renderStatus === "CANCELLED") {
      return <Button danger size="small" onClick={() => setErrorExecution(row)}>Render thất bại</Button>;
    }
    if (row.renderStatus === "QUEUED") {
      return <Tag color="gold">Chờ render agent</Tag>;
    }
    if (row.renderStatus === "PENDING_RETRY") {
      return <Tag color="gold">Chờ chạy lại</Tag>;
    }
    if (row.renderStatus !== "COMPLETED") {
      return <Space direction="vertical" size={2}><Tag color="blue">Đang render · {row.renderProgress}%</Tag><Text type="secondary">{aiVideoStageLabel(row.renderStage)}</Text></Space>;
    }
    if (row.uploadStatus === "FAILED" || row.uploadStatus === "DOWNLOAD_FAILED") {
      return <Space direction="vertical" size={2}><Tag color="red">Upload thất bại</Tag><Text type="danger">{row.errorMessage || "Không có nội dung lỗi"}</Text></Space>;
    }
    if (row.uploadStatus === "COMPLETED") return <Tag color="green">Upload thành công</Tag>;
    if (row.uploadStatus === "UPLOADING") return <Tag color="blue">Đang upload</Tag>;
    if (row.uploadStatus === "WAITING_REVIEW") return <Tag color="green">Render thành công</Tag>;
    return <Tag color="gold">Sẵn sàng upload</Tag>;
  };
  const executionActions = (row) => {
    const available = aiVideoExecutionActionAvailability(row, executions);
    const busy = Boolean(destinationLoading) || Boolean(destinationSaving) || Boolean(confirmingUploadId);
    const retryDisabled = !available.retry || Boolean(retryingExecutionId) || Boolean(deletingExecutionId);
    const editDisabled = !available.edit || Boolean(confirmingUploadId) || Boolean(contentSaving);
    const viewContentDisabled = !available.viewContent || Boolean(contentLoading);
    const videoBusy = Boolean(pendingExecutionAction);
    const destinationDisabled = !available.destination || busy;
    const confirmDisabled = !available.confirm || Boolean(confirmingUploadId) || Boolean(contentSaving);
    const deleteDisabled = !available.delete || Boolean(deletingExecutionId) || Boolean(confirmingUploadId) || Boolean(retryingExecutionId) || Boolean(contentSaving) || Boolean(destinationSaving);
    const runMenuAction = ({ key }) => {
      if (key === "preview") accessExecutionVideo(row, "preview");
      if (key === "download") accessExecutionVideo(row, "download");
      if (key === "content") openContentEditor(row);
      if (key === "retry") Modal.confirm({ title: "Chạy lại video này cho tất cả kênh đang đợi và tái sử dụng các bước đã hoàn thành?", okText: "Chạy lại", cancelText: "Huỷ", onOk: () => retryExecution(row) });
      if (key === "confirm") Modal.confirm({ title: "Xác nhận đưa video này vào hàng đợi upload của kênh đã chọn?", okText: "Xác nhận", cancelText: "Huỷ", onOk: () => confirmUploads([row], row.id) });
      if (key === "delete") Modal.confirm({ title: "Xoá video/bản ghi này khỏi hệ thống? Video đã đăng trên nền tảng ngoài sẽ không bị xoá.", okText: "Xoá", cancelText: "Huỷ", okButtonProps: { danger: true }, onOk: () => deleteExecution(row) });
      if (key.startsWith("destination:")) openDestinationModal(row, key.substring("destination:".length));
    };
    const menu = <Menu onClick={runMenuAction}>
      <Menu.Item key="preview" icon={<PlayCircleOutlined />} disabled={!available.preview || videoBusy}>Xem video</Menu.Item>
      <Menu.Item key="download" icon={<DownloadOutlined />} disabled={!available.download || videoBusy}>Tải video</Menu.Item>
      <Menu.Item key="retry" icon={<RedoOutlined />} disabled={retryDisabled}>Chạy lại video</Menu.Item>
      <Menu.Item key="content" icon={available.viewContent ? <EyeOutlined /> : <EditOutlined />} disabled={available.viewContent ? viewContentDisabled : editDisabled}>{available.viewContent ? "Xem thông tin" : "Sửa nội dung"}</Menu.Item>
      <Menu.SubMenu key="destination" icon={<UploadOutlined />} title="Upload đến kênh khác" disabled={destinationDisabled}>
        <Menu.Item key="destination:YOUTUBE">YouTube</Menu.Item>
        <Menu.Item key="destination:FACEBOOK">Facebook Page</Menu.Item>
        <Menu.Item key="destination:TIKTOK">TikTok</Menu.Item>
      </Menu.SubMenu>
      <Menu.Item key="confirm" icon={<CheckOutlined />} disabled={confirmDisabled}>Xác nhận upload</Menu.Item>
      <Menu.Divider />
      <Menu.Item key="delete" icon={<DeleteOutlined />} danger disabled={deleteDisabled}>Xoá bản ghi</Menu.Item>
    </Menu>;
    return <Dropdown trigger={["click"]} overlay={menu}>
      <Button size="small">Hành động <DownOutlined /></Button>
    </Dropdown>;
  };
  const executionColumns = [
    { title: "VIDEO", width: 100, render: (_, row) => aiVideoExecutionCode(project, row) },
    { title: "KÊNH UPLOAD", width: 130, render: (_, row) => row.channelType ? <><Tag>{row.channelType}</Tag>{row.channelName}</> : "-" },
    { title: "TIÊU ĐỀ", dataIndex: "title", width: 260, render: (value) => <div style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{value || "-"}</div> },
    { title: "LỊCH CHẠY", width: 170, render: (_, row) => formatTime(row.scheduledFor) },
    { title: "TRẠNG THÁI", width: 175, render: (_, row) => executionStatus(row) },
    { title: "HÀNH ĐỘNG", width: 130, fixed: "right", align: "center", render: (_, row) => executionActions(row) },
  ];
  const executionRowSelection = {
    selectedRowKeys: selectedExecutionIds,
    onChange: setSelectedExecutionIds,
    getCheckboxProps: (row) => ({ disabled: Boolean(deletingExecutionId) || Boolean(confirmingUploadId) || ACTIVE_EXECUTION_STATUSES.has(row.renderStatus) }),
  };
  const selectedRows = executions.filter((row) => selectedExecutionIds.includes(row.id));
  const uploadableSelectedRows = selectedRows.filter((row) => row.renderStatus === "COMPLETED" && !row.videoId && row.channelType && row.channelId);
  return <>
    <div className="page-heading">
      <div><Link to={project?.contentPreset === "ILLUSTRATED_STORY" ? "/video-tools/illustrated-video" : "/video-tools/ai-video"}><ArrowLeftOutlined /> Danh sách dự án</Link><Title level={3}>{project.name}</Title><Text type="secondary">Hạn mức: {aiVideoQuotaLabel(quota)}</Text></div>
      <Space wrap>
        {actions.canEdit && <Button loading={pending === "update"} disabled={Boolean(pending)} onClick={saveAll}>Lưu thay đổi</Button>}
        {actions.canGenerate && <Button icon={<ThunderboltOutlined />} loading={pending === "generate"} disabled={Boolean(pending)} onClick={() => runAction("generate")}>Chạy thủ công</Button>}
        {actions.canStart && <Button type="primary" loading={pending === "start"} disabled={Boolean(pending)} onClick={() => runAction("start")}>Bắt đầu dự án</Button>}
        {actions.canStop && <Button danger loading={pending === "stop"} disabled={Boolean(pending)} onClick={() => runAction("stop")}>Dừng dự án</Button>}
      </Space>
    </div>
    <Card className="criclebox" style={{ marginBottom: 16 }}><Space wrap><Tag color={project.automationActive ? "green" : "default"}>{project.automationActive ? "Đang tự động chạy" : "Đã dừng"}</Tag><Text>Lần chạy tiếp: {formatTime(project.nextRunAt)}</Text></Space></Card>
    <Card className="criclebox" title="Thông tin dự án" style={{ marginBottom: 16 }}>
      <AiVideoProjectForm form={projectForm} hideSubmit project={googleTrigger ? { ...project, googleSheetsConfig: googleTrigger } : project} profile={profile} providers={providers} workspaceConnections={workspaceConnections} onSubmit={update} submitting={pending === "update"} readOnly={formReadOnly} />
    </Card>
    <Card className="criclebox" title="Đầu ra và upload" style={{ marginBottom: 16 }}>
      <AiVideoUploadSettings form={uploadForm} hideSubmit project={project} profile={profile} providers={providers} youtubeChannels={youtubeChannels} tiktokAccounts={tiktokAccounts} facebookPages={facebookPages} onSubmit={update} submitting={pending === "update"} readOnly={formReadOnly} />
    </Card>
    <Card className="criclebox" title="Thực hiện" extra={<Space wrap><Popconfirm title={`Xác nhận ${uploadableSelectedRows.length} bản ghi và đưa vào hàng đợi upload?`} okText="Xác nhận" cancelText="Huỷ" disabled={!uploadableSelectedRows.length} onConfirm={() => confirmUploads(uploadableSelectedRows, "BULK")}><Button type="primary" icon={<CheckOutlined />} disabled={!uploadableSelectedRows.length || Boolean(confirmingUploadId) || Boolean(deletingExecutionId)} loading={confirmingUploadId === "BULK"}>Xác nhận upload ({uploadableSelectedRows.length})</Button></Popconfirm><Popconfirm title={`Xoá ${selectedExecutionIds.length} video/bản ghi đã chọn? Video đã đăng trên nền tảng ngoài sẽ không bị xoá.`} okText="Xoá" cancelText="Huỷ" okButtonProps={{ danger: true }} disabled={!selectedExecutionIds.length} onConfirm={deleteSelectedExecutions}><Button danger icon={<DeleteOutlined />} disabled={!selectedExecutionIds.length || Boolean(deletingExecutionId) || Boolean(confirmingUploadId)} loading={deletingExecutionId === "BULK"}>Xoá đã chọn ({selectedExecutionIds.length})</Button></Popconfirm></Space>}><Table rowKey="id" rowSelection={executionRowSelection} columns={executionColumns} dataSource={executions} pagination={{ pageSize: 20 }} scroll={{ x: 1400 }} locale={{ emptyText: "Chưa có lần thực hiện. Bấm Bắt đầu dự án và chờ đến lịch chạy." }} /></Card>
    <Modal title={`Chi tiết lỗi render video ${aiVideoExecutionCode(project, errorExecution)}`} visible={Boolean(errorExecution)} onCancel={() => setErrorExecution(null)} footer={<Button type="primary" onClick={() => setErrorExecution(null)}>Đóng</Button>} width={720} destroyOnClose>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div><Text strong>Server render</Text><div style={{ marginTop: 6 }}><Tag color={errorExecution?.renderAgentId ? "blue" : "default"}>{errorExecution?.renderAgentId || "Chưa được phân bổ"}</Tag></div></div>
        <div><Text strong>Nội dung lỗi</Text><div style={{ marginTop: 6 }}><Text type="danger">{aiVideoErrorMessage(errorExecution?.errorMessage, project.targetDurationSeconds)}</Text></div></div>
        <div><Text strong>Mã lỗi</Text><div style={{ marginTop: 6 }}><Tag color="red">{errorExecution?.errorMessage || "RENDER_FAILED"}</Tag></div></div>
        <div><Text strong>Nội dung lỗi chi tiết</Text><div style={{ marginTop: 6, padding: 12, background: "#f5f5f5", borderRadius: 6, whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxHeight: 320, overflow: "auto" }}>{errorExecution?.errorDetail || "Bản ghi lỗi cũ chưa lưu nội dung kỹ thuật chi tiết. Các lỗi phát sinh sau bản cập nhật sẽ hiển thị đầy đủ tại đây."}</div></div>
      </Space>
    </Modal>
    <Modal title={`${contentReadOnly ? "Xem thông tin" : "Sửa nội dung"} video ${aiVideoExecutionCode(project, contentExecution)}`} visible={Boolean(contentExecution)} onCancel={closeContentEditor} onOk={contentReadOnly ? closeContentEditor : saveContentEditor} confirmLoading={contentSaving} okText={contentReadOnly ? "Đóng" : "Lưu nội dung"} cancelButtonProps={{ style: contentReadOnly ? { display: "none" } : undefined }} width={760} destroyOnClose>
      <Text type="secondary">{contentReadOnly ? "Thông tin đã dùng để upload video." : "Thông tin này dùng chung cho tất cả kênh đang đợi của cùng video. Sau khi xác nhận upload thì không thể sửa."}</Text>
      <Form form={contentForm} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item label="Tiêu đề" name="title" rules={[{ required: true, whitespace: true, message: "Nhập tiêu đề" }, { max: 200, message: "Tối đa 200 ký tự" }]}><Input disabled={contentReadOnly} /></Form.Item>
        <Form.Item label="Từ khoá" name="keywords" rules={[{ required: true, whitespace: true, message: "Nhập từ khoá" }, { max: 2000, message: "Tối đa 2.000 ký tự" }]}><Input.TextArea disabled={contentReadOnly} rows={2} placeholder="Ví dụ: lịch sử World Cup, FIFA, bóng đá" /></Form.Item>
        <Form.Item label="Mô tả" name="description" rules={[{ required: true, whitespace: true, message: "Nhập mô tả" }, { max: 10000, message: "Tối đa 10.000 ký tự" }]}><Input.TextArea disabled={contentReadOnly} rows={6} /></Form.Item>
        <Form.Item label="Thumbnail (không bắt buộc)">
          <Space direction="vertical" size="middle">{!contentReadOnly && <Upload accept="image/*" beforeUpload={chooseThumbnail} maxCount={1} showUploadList={false}><Button icon={<UploadOutlined />}>Chọn ảnh thumbnail</Button></Upload>}{thumbnailFile && <Text type="secondary">Ảnh mới: {thumbnailFile.name}</Text>}{thumbnailPreview && <img src={thumbnailPreview} alt="Thumbnail xem trước" style={{ maxWidth: 320, maxHeight: 180, objectFit: "contain", border: "1px solid #f0f0f0", borderRadius: 6 }} />}</Space>
        </Form.Item>
      </Form>
    </Modal>
    <Modal title={`Upload video ${aiVideoExecutionCode(project, destinationExecution)} đến ${destinationDefinition[destinationPlatform]?.label || "nền tảng"}`} visible={Boolean(destinationExecution)} onCancel={closeDestinationModal} onOk={saveDestinationUpload} confirmLoading={destinationSaving} okText="Lưu" cancelText="Huỷ" width={760} destroyOnClose>
      <Form form={destinationForm} layout="vertical" style={{ marginTop: 8 }} initialValues={{ uploadNow: true }}>
        <Form.Item label={destinationDefinition[destinationPlatform]?.fieldLabel || "Kênh upload"} name="channelId" rules={[{ required: true, message: "Hãy chọn kênh nhận video" }]}>
          <Select showSearch optionFilterProp="label" placeholder="Chọn kênh" options={(destinationDefinition[destinationPlatform]?.items || []).map((item) => ({ value: item.id, label: destinationLabel(item, destinationDefinition[destinationPlatform]?.keys || []) }))} />
        </Form.Item>
        <Form.Item label="Tiêu đề" name="title" rules={[{ required: true, whitespace: true, message: "Nhập tiêu đề" }, { max: 200, message: "Tối đa 200 ký tự" }]}><Input /></Form.Item>
        <Form.Item label="Từ khoá / hashtags" name="keywords" rules={[{ required: true, whitespace: true, message: "Nhập từ khoá hoặc hashtags" }, { max: 2000, message: "Tối đa 2.000 ký tự" }]}><Input.TextArea rows={2} placeholder="Ví dụ: lịch sử, bóng đá, #worldcup" /></Form.Item>
        <Form.Item label="Mô tả" name="description" rules={[{ required: true, whitespace: true, message: "Nhập mô tả" }, { max: 10000, message: "Tối đa 10.000 ký tự" }]}><Input.TextArea rows={5} /></Form.Item>
        <Form.Item label="Thumbnail (không bắt buộc)">
          <Space direction="vertical" size="middle"><Upload accept="image/*" beforeUpload={chooseDestinationThumbnail} maxCount={1} showUploadList={false}><Button icon={<UploadOutlined />}>Chọn ảnh thumbnail</Button></Upload>{destinationThumbnailFile && <Text type="secondary">Ảnh mới: {destinationThumbnailFile.name}</Text>}{destinationThumbnailPreview && <img src={destinationThumbnailPreview} alt="Thumbnail upload" style={{ maxWidth: 320, maxHeight: 180, objectFit: "contain", border: "1px solid #f0f0f0", borderRadius: 6 }} />}</Space>
        </Form.Item>
        <Form.Item label="Upload luôn" name="uploadNow" valuePropName="checked"><Switch /></Form.Item>
        <Form.Item noStyle shouldUpdate={(before, after) => before.uploadNow !== after.uploadNow}>
          {({ getFieldValue }) => !getFieldValue("uploadNow") && <Form.Item label="Thời gian upload" name="scheduledFor" rules={[{ required: true, message: "Hãy chọn thời gian upload" }]}><DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: "100%" }} disabledDate={(date) => date && date.endOf("day").isBefore(moment())} /></Form.Item>}
        </Form.Item>
      </Form>
    </Modal>
  </>;
}
